import React, { Suspense, lazy } from "react";
import "react-toastify/dist/ReactToastify.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import About from "./About";
import Layout from "./layout";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClientProvider } from "@tanstack/react-query";
import LayoutAuth from "./layout/auth";
import { ToastContainer } from "react-toastify";
import { AuthProvider } from "./hooks/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import { ModuleProvider } from "./hooks/module";
import { LocationHistoryStackProvider } from "./hooks/locationHistoryStack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { ThemeModeProvider } from "./themes/modeTheme.tsx";
import { queryClient } from "./utils/reactQuery.ts";
import { towerManagementRoutes } from "./routes/towermanagement.tsx";
import { globalRoutes } from "./routes/global.tsx";
import { financialRoutes } from "./routes/financial.tsx";
import { APIProvider } from "@vis.gl/react-google-maps";
import { helpDeskRoutes } from "./routes/helpDesk.tsx";

const SignInSide = lazy(() => import("./modules/auth"));
const NoMatch = lazy(() => import("./components/noMatch"));
const ModuleSelector = lazy(() => import("./modules/pages/ModuleSelector"));


const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const App: React.FC = () => {
  return (
    <ThemeModeProvider>
      <CssBaseline />
      <ToastContainer />
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={ptBR}
        >
          <Box bgcolor={(t) => t.palette.primary[t.palette.mode]}>
            <Router>
              <AuthProvider>
                <LocationHistoryStackProvider>
                  <ModuleProvider>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute auth={false}>
                            {/* Fica fora do login para não baixar o Google Maps antes da autenticação */}
                            <APIProvider apiKey={API_KEY} version="3.64">
                              <Layout />
                            </APIProvider>
                          </ProtectedRoute>
                        }
                        errorElement={<About />}
                      >
                        <Route
                          index
                          element={
                            <Suspense fallback={<>Carregando...</>}>
                              <ModuleSelector />
                            </Suspense>
                          }
                        />
                        {towerManagementRoutes}
                        {globalRoutes}
                        {helpDeskRoutes}
                        {financialRoutes}
                      <Route
                        path="/notFoundPermission"
                        element={
                          <Suspense fallback={<>Carregando...</>}>
                            <NoMatch />
                          </Suspense>
                        }
                      />
                      </Route>
                      <Route path="/auth" element={<LayoutAuth />}>
                        <Route
                          index
                          element={<Navigate to="/auth/signin" replace />}
                        />
                        <Route
                          path="signin"
                          element={
                            <Suspense fallback={<>Carregando...</>}>
                              <SignInSide />
                            </Suspense>
                          }
                        />
                      </Route>
                    </Routes>
                  </ModuleProvider>
                </LocationHistoryStackProvider>
              </AuthProvider>
            </Router>
          </Box>
        </LocalizationProvider>
      </QueryClientProvider>
    </ThemeModeProvider>
  );
};

export default App;
